import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect } from "react";
import api from "./lib/api";
import { ThemeProvider } from "./components/theme/ThemeProvider";

// Pages
import LandingPage from "./pages/LandingPage";
import FinanceLandingPage from "./pages/FinanceLandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardLayout from "./pages/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import LandingRedirect from "./components/layout/LandingRedirect";
import MenusPage from "./pages/MenusPage";
import MenuDetailPage from "./pages/MenuDetailPage";
import RecipesPage from "./pages/RecipesPage";
import InventoryPage from "./pages/InventoryPage";
import SuppliersPage from "./pages/SuppliersPage";
import CustomersPage from "./pages/CustomersPage";
import ForecastPage from "./pages/ForecastPage";
import ShoppingListsPage from "./pages/ShoppingListsPage";
import TPVIntegrationPage from "./pages/TPVIntegrationPage";
import WebsitePage from "./pages/WebsitePage";
import SettingsPage from "./pages/SettingsPage";
import OnboardingPage from "./pages/OnboardingPage";
import RestaurantLandingPage from "./pages/RestaurantLandingPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import TeamPage from "./pages/TeamPage";
import InvitationPage from "./pages/InvitationPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import FinancePage from "./pages/FinancePage";
import PlatformInvitationPage from "./pages/PlatformInvitationPage";
import DemoPage from "./pages/DemoPage";
import StarterLandingPage from "./pages/StarterLandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import OrdersPage from "./pages/OrdersPage";
import ReceptionsPage from "./pages/ReceptionsPage";
import SalesTodayPage from "./pages/SalesTodayPage";
import CashPage from "./pages/CashPage";
import ProductionPage from "./pages/ProductionPage";

// Plan gating
import PlanGate from "./components/PlanGate";

// Admin pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminInvitations from "./pages/admin/AdminInvitations";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSystemMonitor from "./pages/admin/AdminSystemMonitor";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminAllergens from "./pages/admin/AdminAllergens";
import AdminSuperadmins from "./pages/admin/AdminSuperadmins";
import AdminAIUsage from "./pages/admin/AdminAIUsage";
import AdminAIProviders from "./pages/admin/AdminAIProviders";
import AdminJoinPage from "./pages/admin/AdminJoinPage";
import AdminPlans from "./pages/admin/AdminPlans";
import SubscriptionPage from "./pages/SubscriptionPage";
import HelpPage from "./pages/HelpPage";
import SocialMediaPage from "./pages/SocialMediaPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback (NOT Stripe payment), skip auth check.
    // AuthCallback will handle session exchange and redirect.
    // Stripe redirects include "payment=" param — those are NOT OAuth callbacks.
    const isStripeReturn = window.location.search?.includes("payment=");
    const hasSessionId = window.location.hash?.includes("session_id=") || window.location.search?.includes("session_id=");
    if (hasSessionId && !isStripeReturn) {
      return;
    }

    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // Uses both cookie and Bearer token (fallback for mobile browsers)
      try {
        const userData = await api.getMe();
        setUser(userData);
        setIsAuthenticated(true);
        return;
      } catch (error) {
        // Silently fail — user needs to login
      }

      setIsAuthenticated(false);
    };

    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// SEO: serve sitemap.xml and robots.txt from backend to avoid SPA catch-all
const API_URL = process.env.REACT_APP_BACKEND_URL;
function SeoRedirect({ file }) {
  useEffect(() => {
    window.location.replace(`${API_URL}/api/${file}`);
  }, [file]);
  return null;
}

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  // Check BOTH React Router hash AND window.location directly (mobile compatibility)
  // BUT only for OAuth callbacks — Stripe payment redirects include "payment=" param
  const isStripePaymentReturn = location.search?.includes("payment=") || window.location.search?.includes("payment=");
  const hasSessionInHash = location.hash?.includes("session_id=");
  const hasSessionInSearch = location.search?.includes("session_id=");
  const hasSessionInWindowHash = window.location.hash?.includes("session_id=");
  const hasSessionInWindowSearch = window.location.search?.includes("session_id=");

  if (!isStripePaymentReturn && (hasSessionInHash || hasSessionInSearch || hasSessionInWindowHash || hasSessionInWindowSearch)) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* SEO: redirect to backend-served files to avoid SPA catch-all */}
      <Route path="/sitemap.xml" element={<SeoRedirect file="sitemap.xml" />} />
      <Route path="/robots.txt" element={<SeoRedirect file="robots.txt" />} />

      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/features/finance" element={<FinanceLandingPage />} />
      <Route path="/precio-starter" element={<StarterLandingPage />} />
      <Route path="/starter" element={<StarterLandingPage />} />
      <Route path="/legal/privacidad/:restaurantId" element={<PrivacyPolicyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* Public restaurant landing page */}
      <Route path="/r/:slug" element={<RestaurantLandingPage />} />
      {/* Customer loyalty portal */}
      <Route path="/loyalty/:restaurantId" element={<CustomerPortalPage />} />
      {/* Team invitation (public) */}
      <Route path="/invitar/:token" element={<InvitationPage />} />
      {/* Platform invitation (invite-only onboarding) */}
      <Route path="/join/:token" element={<PlatformInvitationPage />} />
      <Route path="/demo" element={<DemoPage />} />

      {/* Admin routes */}
      <Route path="/admin/join/:token" element={<AdminJoinPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="invitations" element={<AdminInvitations />} />
        <Route path="superadmins" element={<AdminSuperadmins />} />
        <Route path="monitor" element={<AdminSystemMonitor />} />
        <Route path="tasks" element={<AdminTasks />} />
        <Route path="allergens" element={<AdminAllergens />} />
              <Route path="ai-usage" element={<AdminAIUsage />} />
              <Route path="ai-providers" element={<AdminAIProviders />} />
              <Route path="plans" element={<AdminPlans />} />
      </Route>
      
      {/* Protected routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/app" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<LandingRedirect />} />
        <Route path="menus" element={<MenusPage />} />
        <Route path="menus/:menuId" element={<MenuDetailPage />} />
        <Route path="recipes" element={
          <PlanGate moduleKey="recipes" label="Recetas"
            description="Crea, escala y cuesta recetas automáticamente. Mermas, rendimiento y coste por plato calculados en tiempo real."
            benefits={["Fichas técnicas completas", "Escalado automático por raciones", "Coste por plato en vivo", "Generación IA de recetas"]}>
            <RecipesPage />
          </PlanGate>
        } />
        <Route path="inventory" element={
          <PlanGate moduleKey="inventory" label="Inventario"
            description="Control de stock en tiempo real, mermas, conteos periódicos y alertas de caídas no explicadas."
            benefits={["Stock vivo con conteos", "Alertas de mermas IA", "Histórico de movimientos", "Integración con proveedores"]}>
            <InventoryPage />
          </PlanGate>
        } />
        <Route path="suppliers" element={
          <PlanGate moduleKey="suppliers" label="Proveedores"
            description="Gestiona tu red de proveedores, pedidos, histórico de precios y alertas de subidas."
            benefits={["Catálogo de proveedores", "Histórico de precios por producto", "Alertas de subidas de precio", "Pedidos recurrentes automáticos"]}>
            <SuppliersPage />
          </PlanGate>
        } />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="forecast" element={
          <PlanGate moduleKey="forecast" label="Previsiones IA"
            description="Predicciones de ventas, pedidos y staffing con IA basadas en tu histórico y el clima."
            benefits={["Previsión de ventas semanal", "Sugerencias de compra", "Alertas de sobre-stock", "Predicción de rotación"]}
            requiredPlan="Pro">
            <ForecastPage />
          </PlanGate>
        } />
        <Route path="shopping-lists" element={
          <PlanGate moduleKey="shopping_lists" label="Listas de Compra"
            description="Genera listas de compra automáticas en base a tu stock actual y recetas planificadas."
            benefits={["Listas automáticas por proveedor", "Exportación a PDF/Excel", "Comparativa de precios", "Seguimiento de pedidos abiertos"]}>
            <ShoppingListsPage />
          </PlanGate>
        } />
        <Route path="tpv" element={
          <PlanGate moduleKey="tpv" label="Integración TPV"
            description="Conecta tu TPV (Lightspeed, Square, Revo, etc.) y sincroniza ventas, stock y menú automáticamente."
            benefits={["Sync de ventas en tiempo real", "Actualización automática de menú", "Consolidación multi-local", "Informes unificados"]}>
            <TPVIntegrationPage />
          </PlanGate>
        } />
        <Route path="website" element={<WebsitePage />} />
        <Route path="social" element={
          <PlanGate moduleKey="social" label="Redes Sociales"
            description="Programa y publica posts en Instagram/Facebook con imágenes IA a partir de tus platos."
            benefits={["Calendario editorial", "Generación de posts con IA", "Programación multi-cuenta", "Analíticas de engagement"]}>
            <SocialMediaPage />
          </PlanGate>
        } />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="orders" element={
          <PlanGate moduleKey="shopping_lists" label="Pedidos"
            description="Genera, envía y recibe pedidos a tus proveedores con histórico completo."
            benefits={["Borradores agrupados por proveedor", "Envío por email automático", "Recepción con incidencias", "Histórico de pedidos enviados"]}>
            <OrdersPage />
          </PlanGate>
        } />
        <Route path="receptions" element={
          <PlanGate moduleKey="inventory" label="Recepciones"
            description="Registra recepciones e incidencias para alimentar las puntuaciones de proveedores."
            benefits={["Recepción estructurada", "Incidencias por ítem", "Historial completo", "Scorecard automático de proveedores"]}>
            <ReceptionsPage />
          </PlanGate>
        } />
        <Route path="sales-today" element={<SalesTodayPage />} />
        <Route path="cash" element={
          <PlanGate moduleKey="finance" label="Caja y Cierres"
            description="Apertura, cierre de caja y cierres operativos diarios."
            benefits={["Arqueo de caja", "Cierre de ventas con contraste TPV", "Cierre de inventario", "Cierre de producción"]}>
            <CashPage />
          </PlanGate>
        } />
        <Route path="production" element={
          <PlanGate moduleKey="recipes" label="Producción"
            description="Mise en place, batches y caducidades — descontados automáticamente del inventario."
            benefits={["Registro diario de producción", "Vencimientos automáticos", "Vinculación a recetas", "Alertas de caducidad"]}>
            <ProductionPage />
          </PlanGate>
        } />
        <Route path="team" element={
          <PlanGate moduleKey="team" label="Equipo"
            description="Invita a tu equipo con roles granulares (encargado, cocina, camarero, contabilidad)."
            benefits={["Roles configurables", "Permisos por módulo", "Invitaciones por email", "Auditoría de acciones"]}>
            <TeamPage />
          </PlanGate>
        } />
        <Route path="analytics" element={
          <PlanGate moduleKey="analytics" label="Análisis"
            description="KPIs, ratios, cohortes y márgenes con visualizaciones profesionales."
            benefits={["KPIs de negocio en tiempo real", "Márgenes por plato/categoría", "Cohortes de clientes", "Comparativas período a período"]}>
            <AnalyticsPage />
          </PlanGate>
        } />
        <Route path="finance" element={
          <PlanGate moduleKey="finance" label="Contabilidad"
            description="Facturación, OCR automático, cierres fiscales Z-close, libro anual SHA-256 y notificaciones proactivas."
            benefits={["OCR de facturas sin mover un dedo", "Cierres diarios/mensuales/anuales automáticos", "Libro anual con cadena SHA-256 verificable", "Alertas fiscales proactivas por email"]}>
            <FinancePage />
          </PlanGate>
        } />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="help" element={<HelpPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
